import { apiClient } from '../client';
import { 
  ActionTransformUI, 
  ActionTransformValues, 
  ActionTransformDefaults,
  ValidationResult, 
  ComponentChangeEvent, 
  DropdownOption 
} from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('WorkflowsService');
import { telemetryService } from '../index';

export class WorkflowsService {
  /**
   * Get UI configuration for a specific action type
   */
  async getActionUIConfiguration(actionType: string): Promise<ActionTransformUI> {
    try {
      logger.debug('Fetching action UI configuration for:', actionType);
      logger.debug('🔗 API Call: GET /v1/workflow/action-ui/' + actionType);
      
      const config = await apiClient.get<ActionTransformUI>(`/v1/workflow/action-ui/${actionType}`);
      
      logger.debug('Action UI configuration fetched successfully:', {
        actionType,
        pagesCount: config.pages?.length || 0,
        actionName: config.name
      });

      logger.debug('✅ UI config API response:', {
        actionType,
        actionName: config.name,
        pagesCount: config.pages?.length || 0,
        pages: config.pages?.map(p => ({ id: p.id, title: p.title, componentCount: p.components.length }))
      });

      return config;
    } catch (error) {
      logger.error('Failed to fetch action UI configuration:', error);
      telemetryService.trackError(error as Error, 'action-ui-fetch', {
        endpoint: `/v1/workflow/action-ui/${actionType}`,
        actionType,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Get current values for a specific action instance
   */
  async getActionValues(workflowPath: string, actionId: string): Promise<ActionTransformValues> {
    try {
      logger.debug('Fetching action values for:', { workflowPath, actionId });
      logger.debug('🔗 API Call: GET /v1/workflow/action-values/' + actionId);
      
      const values = await apiClient.get<ActionTransformValues>(
        `/v1/workflow/action-values/${actionId}`,
        { params: { path: workflowPath } }
      );
      
      logger.debug('Action values fetched successfully:', {
        actionId,
        actionType: values.type,
        valuesCount: Object.keys(values.values || {}).length
      });

      return values;
    } catch (error) {
      logger.error('Failed to fetch action values:', error);
      telemetryService.trackError(error as Error, 'action-values-fetch', {
        endpoint: `/v1/workflow/action-values/${actionId}`,
        workflowPath,
        actionId,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Get default values for a new action of the specified type
   */
  async getActionDefaults(actionType: string): Promise<ActionTransformDefaults> {
    try {
      logger.debug('Fetching default values for action type:', actionType);
      logger.debug('🔗 API Call: GET /v1/workflow/action-defaults/' + actionType);
      
      const defaults = await apiClient.get<ActionTransformDefaults>(`/v1/workflow/action-defaults/${actionType}`);
      
      logger.debug('Action defaults fetched successfully:', {
        actionType,
        valuesCount: Object.keys(defaults.values || {}).length
      });

      return defaults;
    } catch (error) {
      logger.error('Failed to fetch action defaults:', error);
      telemetryService.trackError(error as Error, 'action-defaults-fetch', {
        endpoint: `/v1/workflow/action-defaults/${actionType}`,
        actionType,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Validate action configuration
   */
  async validateAction(actionType: string, values: Record<string, unknown>): Promise<ValidationResult> {
    try {
      logger.debug('Validating action configuration:', { actionType, values });
      logger.debug('🔗 API Call: POST /v1/workflow/validate-action/' + actionType);
      
      const result = await apiClient.post<ValidationResult>(
        `/v1/workflow/validate-action/${actionType}`,
        values
      );
      
      logger.debug('Action validation completed:', {
        actionType,
        valid: result.valid,
        errorCount: result.errors ? Object.keys(result.errors).length : 0,
        warningCount: result.warnings ? Object.keys(result.warnings).length : 0
      });

      return result;
    } catch (error) {
      logger.error('Failed to validate action:', error);
      telemetryService.trackError(error as Error, 'action-validation', {
        endpoint: `/v1/workflow/validate-action/${actionType}`,
        actionType,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Handle component change events
   */
  async handleComponentChange(event: ComponentChangeEvent): Promise<DropdownOption[]> {
    try {
      logger.debug('Handling component change:', event);
      logger.debug('🔗 API Call: POST /v1/workflow/component-change');
      
      const options = await apiClient.post<DropdownOption[]>('/v1/workflow/component-change', event);
      
      logger.debug('Component change handled successfully:', {
        actionType: event.actionType,
        componentId: event.componentId,
        optionsCount: options.length
      });

      return options;
    } catch (error) {
      logger.error('Failed to handle component change:', error);
      telemetryService.trackError(error as Error, 'action-component-change', {
        endpoint: '/v1/workflow/component-change',
        actionType: event.actionType,
        componentId: event.componentId,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Get dropdown options for a specific component
   */
  async getDropdownOptions(
    actionType: string, 
    componentId: string, 
    values?: Record<string, unknown>
  ): Promise<DropdownOption[]> {
    try {
      logger.debug('Fetching dropdown options:', { actionType, componentId, values });
      logger.debug('🔗 API Call: POST /v1/workflow/dropdown-options/' + actionType + '/' + componentId);
      
      const options = await apiClient.post<DropdownOption[]>(
        `/v1/workflow/dropdown-options/${actionType}/${componentId}`,
        values || {}
      );
      
      logger.debug('Dropdown options fetched successfully:', {
        actionType,
        componentId,
        optionsCount: options.length
      });

      return options;
    } catch (error) {
      logger.error('Failed to fetch dropdown options:', error);
      telemetryService.trackError(error as Error, 'action-dropdown-options', {
        endpoint: `/v1/workflow/dropdown-options/${actionType}/${componentId}`,
        actionType,
        componentId,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
}

// Create and export a default instance
export const workflowsService = new WorkflowsService();
